import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { typography } from '../../styles/typography';
import { Card, PrimaryButton } from '../ui';
import {
  emptyTipTapNotes,
  formatScopeApiError as formatScopeApiErrorImpl,
  interiorScopeApi,
} from './interiorScopeApi';
import {
  buildWizardSteps,
  collectKnownQuestionIdsForRoomType,
  collectNumberFieldIdsForRoomType,
  collectQuestionOptionsForRoomType,
  finalizeAnswersForRoomType,
  isApiNumberFieldId,
  isForbiddenAnswerForRoomType,
  isStringNotNumberFieldId,
  normalizeAreasPayload,
  sanitizeAnswersForApi,
  sanitizeProgramForApi,
} from './scopeTemplateEngine';
import {
  SINGLE_SELECT_ANSWER_IDS,
  coerceAnswerNumberValue,
  coerceAnswerNumbersForUi,
  coerceProgramNumbersForUi,
  coerceToApiString,
  forceAnswerNumbers,
  isEmptyAnswerNumber,
  normalizeSingleSelectAnswers,
  toUiNumberValue,
} from './scopeNumberCoercion';
import TemplateScopeForm, { TemplateScopeNav } from './TemplateScopeForm';

const formatScopeApiError = (err) =>
  typeof formatScopeApiErrorImpl === 'function'
    ? formatScopeApiErrorImpl(err)
    : err?.message || 'Request failed';

const AUTOSAVE_MS = 5000;

const COUNT_KEYS = [
  'bedroom_count',
  'balcony_count',
  'help_room_count',
  'store_room_count',
];

/** Stable fingerprint so identical docs do not re-PATCH. */
const scopeDocFingerprint = (doc) => {
  if (!doc) return '';
  try {
    return JSON.stringify({
      program: doc.program || {},
      areas: Object.fromEntries(
        Object.entries(doc.areas || {}).map(([k, a]) => [
          k,
          { answers: a?.answers || {}, notes: a?.notes || null },
        ])
      ),
      rooms: (doc.rooms || []).map((r) => ({
        id: r.instance_id || r.instanceId,
        label: r.label,
        answers: r.answers || {},
        notes: r.notes || null,
      })),
      documentNotes: doc.documentNotes || null,
    });
  } catch {
    return String(Date.now());
  }
};

/**
 * Build a minimal PATCH body from dirty section keys.
 * section keys: program | document_notes | areas | area:<type> | rooms | room:<id>
 */
const buildDirtyPatchBody = (snapshot, dirtySections) => {
  if (!snapshot || !dirtySections?.size) return null;
  const body = {};
  const sections = dirtySections;

  if (sections.has('program') || sections.has('all')) {
    body.program = snapshot.program;
  }

  if (sections.has('document_notes') || sections.has('all')) {
    body.document_notes = snapshot.documentNotes || emptyTipTapNotes();
  }

  const areaKeys = [];
  if (sections.has('areas') || sections.has('all')) {
    areaKeys.push(...Object.keys(snapshot.areas || {}));
  } else {
    sections.forEach((key) => {
      if (key.startsWith('area:')) areaKeys.push(key.slice(5));
    });
  }
  if (areaKeys.length) {
    const areas = {};
    areaKeys.forEach((roomType) => {
      const area = snapshot.areas?.[roomType];
      if (!area) return;
      areas[roomType] = {
        room_type: roomType,
        answers: area.answers || {},
        notes: area.notes || emptyTipTapNotes(),
      };
    });
    if (Object.keys(areas).length) body.areas = areas;
  }

  const wantAllRooms = sections.has('rooms') || sections.has('all');
  const roomIds = new Set();
  if (!wantAllRooms) {
    sections.forEach((key) => {
      if (key.startsWith('room:')) roomIds.add(key.slice(5));
    });
  }
  if (wantAllRooms || roomIds.size) {
    const rooms = (snapshot.rooms || [])
      .filter((r) => {
        const id = r.instance_id || r.instanceId;
        return id && (wantAllRooms || roomIds.has(id));
      })
      .map((r) => ({
        instance_id: r.instance_id || r.instanceId,
        label: r.label,
        answers: r.answers || {},
        notes: r.notes || emptyTipTapNotes(),
      }));
    if (rooms.length) body.rooms = rooms;
  }

  return Object.keys(body).length ? body : null;
};

const countsFromProgram = (program = {}) =>
  COUNT_KEYS.reduce((acc, key) => {
    acc[key] = Number(program[key]) || 0;
    return acc;
  }, {});

/**
 * Keep in-progress local edits when applying a server snapshot.
 * Prevents autosave/expand responses from wiping keystrokes mid-type.
 * Empty local strings must not overwrite server numbers for API number fields.
 */
const preferValue = (localVal, serverVal, key) => {
  if (!isApiNumberFieldId(key)) {
    return localVal !== undefined ? localVal : serverVal;
  }
  if (!isEmptyAnswerNumber(localVal)) {
    return toUiNumberValue(localVal);
  }
  if (!isEmptyAnswerNumber(serverVal)) {
    return toUiNumberValue(serverVal);
  }
  return '';
};

const mergeAnswersPreferringNumbers = (serverAnswers = {}, localAnswers = {}) => {
  const keys = new Set([
    ...Object.keys(serverAnswers || {}),
    ...Object.keys(localAnswers || {}),
  ]);
  const out = {};
  keys.forEach((key) => {
    out[key] = preferValue(localAnswers?.[key], serverAnswers?.[key], key);
  });
  return coerceAnswerNumbersForUi(out);
};

const scrubScopeForUi = (scope) => {
  if (!scope) return scope;
  const areas = {};
  Object.entries(scope.areas || {}).forEach(([roomType, area]) => {
    areas[roomType] = {
      ...area,
      answers: coerceAnswerNumbersForUi(area?.answers || {}),
    };
  });
  const rooms = (scope.rooms || []).map((room) => ({
    ...room,
    answers: coerceAnswerNumbersForUi(room.answers || {}),
  }));
  return {
    ...scope,
    program: coerceProgramNumbersForUi(scope.program || {}),
    areas,
    rooms,
  };
};

const mergePreservingLocalEdits = (local, server) => {
  if (!server) return local;
  if (!local) return server;

  const rooms = (server.rooms || []).map((sRoom) => {
    const sId = sRoom.instance_id || sRoom.instanceId;
    const lRoom = (local.rooms || []).find((r) => {
      const lId = r.instance_id || r.instanceId;
      if (lId && sId && lId === sId) return true;
      return (
        r.room_type === sRoom.room_type &&
        Number(r.index) === Number(sRoom.index)
      );
    });
    if (!lRoom) return sRoom;
    return {
      ...sRoom,
      label: lRoom.label ?? sRoom.label,
      answers: mergeAnswersPreferringNumbers(
        sRoom.answers || {},
        lRoom.answers || {}
      ),
      notes: lRoom.notes || sRoom.notes || emptyTipTapNotes(),
    };
  });

  const areas = { ...(server.areas || {}) };
  Object.entries(local.areas || {}).forEach(([roomType, area]) => {
    const serverArea = areas[roomType] || {};
    areas[roomType] = {
      ...serverArea,
      ...area,
      room_type: roomType,
      answers: mergeAnswersPreferringNumbers(
        serverArea.answers || {},
        area?.answers || {}
      ),
      notes: area?.notes || serverArea.notes || emptyTipTapNotes(),
    };
  });

  const programKeys = new Set([
    ...Object.keys(server.program || {}),
    ...Object.keys(local.program || {}),
  ]);
  const program = {};
  programKeys.forEach((key) => {
    program[key] = preferValue(
      local.program?.[key],
      server.program?.[key],
      key
    );
  });

  return {
    ...server,
    program: coerceProgramNumbersForUi(program),
    areas,
    rooms,
    documentNotes:
      local.documentNotes || server.documentNotes || emptyTipTapNotes(),
  };
};

const CreateProject = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId: scopeIdParam } = useParams();
  const siteId =
    location.state?.siteId ||
    new URLSearchParams(location.search).get('siteId') ||
    '';
  const isEditMode = Boolean(scopeIdParam);

  const [template, setTemplate] = useState(null);
  const [scopeDoc, setScopeDoc] = useState(null);
  const [activeStepId, setActiveStepId] = useState('program:A');
  const [completedStepIds, setCompletedStepIds] = useState(() => new Set());
  const [resolvedSiteId, setResolvedSiteId] = useState(siteId || '');
  const [sites, setSites] = useState([]);
  const [loadingProject, setLoadingProject] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [autosaveState, setAutosaveState] = useState('');
  const [error, setError] = useState('');
  const skipAutosave = useRef(true);
  const lastCounts = useRef(countsFromProgram());
  const scopeDocRef = useRef(null);
  const formScrollRef = useRef(null);
  const savingRef = useRef(false);
  const pendingSaveRef = useRef(false);
  const dirtyRef = useRef(false);
  const dirtySectionsRef = useRef(new Set());
  const lastSavedFingerprintRef = useRef('');
  const lastSavedDocRef = useRef(null);
  const persistPatchRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const loadingProjectRef = useRef(true);
  const readOnlyRef = useRef(false);

  useEffect(() => {
    scopeDocRef.current = scopeDoc;
  }, [scopeDoc]);

  const readOnly = scopeDoc?.status === 'submitted';
  const effectiveSiteId = resolvedSiteId || siteId;

  useEffect(() => {
    loadingProjectRef.current = loadingProject;
  }, [loadingProject]);

  useEffect(() => {
    readOnlyRef.current = readOnly;
  }, [readOnly]);

  const scheduleAutosave = useCallback(() => {
    if (skipAutosave.current || loadingProjectRef.current || readOnlyRef.current) {
      return;
    }
    if (!dirtyRef.current) return;
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null;
      if (
        skipAutosave.current ||
        loadingProjectRef.current ||
        readOnlyRef.current
      ) {
        return;
      }
      if (!dirtyRef.current) return;
      const fp = scopeDocFingerprint(scopeDocRef.current);
      if (fp && fp === lastSavedFingerprintRef.current) {
        dirtyRef.current = false;
        dirtySectionsRef.current.clear();
        return;
      }
      persistPatchRef.current?.(scopeDocRef.current);
    }, AUTOSAVE_MS);
  }, []);

  const markDirty = useCallback((sectionKey = 'all') => {
    dirtyRef.current = true;
    if (sectionKey === 'all') {
      dirtySectionsRef.current.add('program');
      dirtySectionsRef.current.add('document_notes');
      dirtySectionsRef.current.add('areas');
      dirtySectionsRef.current.add('rooms');
    } else {
      dirtySectionsRef.current.add(sectionKey);
    }
    scheduleAutosave();
  }, [scheduleAutosave]);

  const steps = useMemo(
    () => (template && scopeDoc ? buildWizardSteps(template, scopeDoc) : []),
    [template, scopeDoc]
  );

  const activeStep = useMemo(
    () => steps.find((s) => s.id === activeStepId) || steps[0] || null,
    [steps, activeStepId]
  );

  const goToStep = useCallback((stepId) => {
    if (!stepId) return;
    setActiveStepId(stepId);
    // Scroll the form panel so the next section starts at the top
    requestAnimationFrame(() => {
      formScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    });
  }, []);

  const continueToNextSection = useCallback(() => {
    const idx = steps.findIndex((s) => s.id === activeStepId);
    if (idx < 0 || idx >= steps.length - 1) return;
    const currentId = steps[idx]?.id;
    if (currentId) {
      setCompletedStepIds((prev) => {
        if (prev.has(currentId)) return prev;
        const next = new Set(prev);
        next.add(currentId);
        return next;
      });
    }
    goToStep(steps[idx + 1].id);
  }, [steps, activeStepId, goToStep]);

  const navCompletedStepIds = useMemo(() => {
    if (readOnly) {
      return new Set(steps.map((s) => s.id));
    }
    return completedStepIds;
  }, [readOnly, steps, completedStepIds]);

  useEffect(() => {
    if (siteId) {
      setResolvedSiteId(siteId);
      return undefined;
    }
    let active = true;
    interiorScopeApi
      .getSites()
      .then((list) => {
        if (!active) return;
        const normalized = Array.isArray(list) ? list : [];
        setSites(normalized);
        if (normalized[0]?.id) setResolvedSiteId(normalized[0].id);
      })
      .catch(() => {
        if (!active) return;
        setError('Could not load sites. Open Scope Sheets and pick a site first.');
      });
    return () => {
      active = false;
    };
  }, [siteId]);

  useEffect(() => {
    if (!steps.length) return;
    if (!steps.some((s) => s.id === activeStepId)) {
      setActiveStepId(steps[0].id);
    }
  }, [steps, activeStepId]);

  const applyScope = useCallback((scope, { preserveLocal = false, markClean = false } = {}) => {
    setScopeDoc((prev) => {
      const merged = preserveLocal
        ? mergePreservingLocalEdits(prev, scope)
        : scope;
      const next = scrubScopeForUi(merged);
      scopeDocRef.current = next;
      lastCounts.current = countsFromProgram(next?.program);
      if (markClean) {
        dirtyRef.current = false;
        dirtySectionsRef.current.clear();
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let active = true;

    const boot = async () => {
      if (!effectiveSiteId) {
        setLoadingProject(false);
        return;
      }

      setLoadingProject(true);
      setError('');
      skipAutosave.current = true;

      try {
        const tpl = await interiorScopeApi.getLatestTemplate();
        if (!active) return;
        setTemplate(tpl);

        if (isEditMode) {
          const scope = await interiorScopeApi.getScope(
            effectiveSiteId,
            scopeIdParam
          );
          if (!active) return;
          applyScope(scope);
          lastSavedFingerprintRef.current = scopeDocFingerprint(scope);
          lastSavedDocRef.current = {
            program: scope.program || {},
            areas: scope.areas || {},
            rooms: scope.rooms || [],
            documentNotes: scope.documentNotes || emptyTipTapNotes(),
          };
          setActiveStepId('program:A');
          setCompletedStepIds(new Set());
        } else {
          const created = await interiorScopeApi.createScope(effectiveSiteId, {
            template_version: tpl?.version || '1.0.0',
          });
          if (!active) return;
          navigate(
            `/projects/${created.scopeId}/edit?siteId=${encodeURIComponent(effectiveSiteId)}`,
            {
              replace: true,
              state: { siteId: effectiveSiteId },
            }
          );
          return;
        }
      } catch (err) {
        if (!active) return;
        setError(formatScopeApiError(err));
      } finally {
        if (active) {
          setLoadingProject(false);
          setTimeout(() => {
            skipAutosave.current = false;
          }, 400);
        }
      }
    };

    boot();
    return () => {
      active = false;
    };
  }, [applyScope, effectiveSiteId, isEditMode, navigate, scopeIdParam]);

  const runExpandIfNeeded = useCallback(
    async (program, confirmTruncate = false) => {
      if (!effectiveSiteId || !scopeIdParam || readOnly) return null;
      const next = countsFromProgram(program);
      const prev = lastCounts.current;
      const changed = COUNT_KEYS.some((k) => next[k] !== prev[k]);
      if (!changed) return null;

      try {
        const expanded = await interiorScopeApi.expandScope(
          effectiveSiteId,
          scopeIdParam,
          {
            program: sanitizeProgramForApi(
              {
                ...program,
                bedroom_count: next.bedroom_count,
                balcony_count: next.balcony_count,
                help_room_count: next.help_room_count,
                store_room_count: next.store_room_count,
              },
              template
            ),
            confirm_truncate: confirmTruncate,
          }
        );
        lastCounts.current = next;
        if (expanded?.scopeId) {
          applyScope(expanded, { preserveLocal: true });
        } else {
          const fresh = await interiorScopeApi.getScope(
            effectiveSiteId,
            scopeIdParam
          );
          applyScope(fresh, { preserveLocal: true });
        }
        return expanded;
      } catch (err) {
        if (
          err.status === 409 &&
          (err.code === 'truncate_requires_confirm' ||
            err.responseData?.error === 'truncate_requires_confirm')
        ) {
          const ok = window.confirm(
            'Reducing room counts will remove room data. Continue?'
          );
          if (ok) return runExpandIfNeeded(program, true);
        }
        throw err;
      }
    },
    [applyScope, effectiveSiteId, readOnly, scopeIdParam, template]
  );

  const persistPatch = useCallback(
    async (docArg, options = {}) => {
      if (!effectiveSiteId || !scopeIdParam || readOnly) return;
      const { waitForIdle = false } = options;

      // Submit must wait for any in-flight autosave, then write sanitized numbers.
      if (waitForIdle) {
        const started = Date.now();
        while (savingRef.current && Date.now() - started < 30000) {
          await new Promise((resolve) => setTimeout(resolve, 50));
        }
        // Drop queued autosaves so they don't race the submit write.
        pendingSaveRef.current = false;
      }

      if (savingRef.current) {
        pendingSaveRef.current = true;
        if (waitForIdle) {
          throw new Error('Could not save before submit — please try again');
        }
        return;
      }

      const doc = docArg || scopeDocRef.current;
      if (!doc) return;

      const fingerprint = scopeDocFingerprint(doc);
      if (
        fingerprint &&
        fingerprint === lastSavedFingerprintRef.current &&
        !waitForIdle
      ) {
        dirtyRef.current = false;
        dirtySectionsRef.current.clear();
        return;
      }

      savingRef.current = true;
      setAutosaveState('Saving…');
      try {
        // Always save the latest local doc (not a stale effect snapshot).
        let latest = scopeDocRef.current || doc;
        const program = sanitizeProgramForApi(latest.program || {}, template);

        // Expand only when program counts may have changed (or on submit).
        // Typing kitchen/room answers must not POST /expand.
        const programMayHaveChanged =
          waitForIdle ||
          dirtySectionsRef.current.has('program') ||
          dirtySectionsRef.current.has('all');
        const expanded = programMayHaveChanged
          ? await runExpandIfNeeded(program)
          : null;
        latest = scopeDocRef.current || latest;

        // GET only when rooms still lack instance_ids (e.g. after expand).
        // Routine answer autosaves already have ids — skip the GET storm.
        const roomsNeedIds = (latest.rooms || []).some(
          (r) => !(r.instance_id || r.instanceId)
        );
        let fresh = latest;
        if (roomsNeedIds) {
          fresh = await interiorScopeApi.getScope(
            effectiveSiteId,
            scopeIdParam
          );
        }
        // Re-apply local unsaved edits on top of fresh instance ids
        const knownIdsByRoomType = new Map();
        const optionMapByRoomType = new Map();
        const numberIdsByRoomType = new Map();
        const knownIdsFor = (roomType) => {
          if (!roomType) return null;
          if (!knownIdsByRoomType.has(roomType)) {
            knownIdsByRoomType.set(
              roomType,
              collectKnownQuestionIdsForRoomType(template, roomType)
            );
          }
          return knownIdsByRoomType.get(roomType);
        };
        const optionMapFor = (roomType) => {
          if (!roomType) return null;
          if (!optionMapByRoomType.has(roomType)) {
            optionMapByRoomType.set(
              roomType,
              collectQuestionOptionsForRoomType(template, roomType)
            );
          }
          return optionMapByRoomType.get(roomType);
        };
        const numberIdsFor = (roomType) => {
          if (!roomType) return null;
          if (!numberIdsByRoomType.has(roomType)) {
            const ids = collectNumberFieldIdsForRoomType(template, roomType);
            // Also mark dimension/count ids from the known question set
            // (template may type them as text while submit requires numbers).
            const known = knownIdsFor(roomType);
            if (known) {
              known.forEach((id) => {
                if (isApiNumberFieldId(id)) ids.add(id);
              });
            }
            // Option fields must never be in the number set — except dimension/qty
            // ids that submit still types as numbers.
            const options = optionMapFor(roomType);
            if (options) {
              options.forEach((_vals, id) => {
                if (!isApiNumberFieldId(id) || isStringNotNumberFieldId(id)) {
                  ids.delete(id);
                }
              });
            }
            [...ids].forEach((id) => {
              if (isStringNotNumberFieldId(id)) ids.delete(id);
              if (SINGLE_SELECT_ANSWER_IDS.has(id)) ids.delete(id);
              if (isForbiddenAnswerForRoomType(roomType, id)) ids.delete(id);
            });
            numberIdsByRoomType.set(roomType, ids);
          }
          return numberIdsByRoomType.get(roomType);
        };

        const mergedRooms = (latest.rooms || [])
          .map((local) => {
            const id = local.instance_id || local.instanceId;
            if (!id) {
              const byIndex = (fresh.rooms || []).find(
                (r) =>
                  r.room_type === local.room_type &&
                  Number(r.index) === Number(local.index)
              );
              const resolvedId =
                byIndex?.instance_id || byIndex?.instanceId || null;
              if (!resolvedId) return null;
              const roomType = local.room_type || byIndex?.room_type;
              return {
                instance_id: resolvedId,
                room_type: roomType,
                label: local.label ?? byIndex?.label,
                answers: finalizeAnswersForRoomType(
                  roomType,
                  sanitizeAnswersForApi(
                    {
                      ...(byIndex?.answers || {}),
                      ...(local.answers || {}),
                    },
                    knownIdsFor(roomType),
                    optionMapFor(roomType),
                    numberIdsFor(roomType)
                  )
                ),
                notes: local.notes || byIndex?.notes || emptyTipTapNotes(),
              };
            }
            const server = (fresh.rooms || []).find(
              (r) => (r.instance_id || r.instanceId) === id
            );
            const roomType = local.room_type || server?.room_type;
            return {
              instance_id: id,
              room_type: roomType,
              label: local.label ?? server?.label,
              answers: finalizeAnswersForRoomType(
                roomType,
                sanitizeAnswersForApi(
                  { ...(server?.answers || {}), ...(local.answers || {}) },
                  knownIdsFor(roomType),
                  optionMapFor(roomType),
                  numberIdsFor(roomType)
                )
              ),
              notes: local.notes || server?.notes || emptyTipTapNotes(),
            };
          })
          .filter(Boolean);

        const areas = {};
        const areaKeys = new Set([
          ...Object.keys(latest.areas || {}),
          ...Object.keys(fresh.areas || {}),
        ]);
        areaKeys.forEach((roomType) => {
          const localArea = latest.areas?.[roomType];
          const serverArea = fresh.areas?.[roomType];
          areas[roomType] = {
            room_type: roomType,
            answers: finalizeAnswersForRoomType(
              roomType,
              sanitizeAnswersForApi(
                {
                  ...(serverArea?.answers || {}),
                  ...(localArea?.answers || {}),
                },
                knownIdsFor(roomType),
                optionMapFor(roomType),
                numberIdsFor(roomType)
              )
            ),
            notes:
              localArea?.notes ||
              serverArea?.notes ||
              emptyTipTapNotes(),
          };
        });

        const sanitizedSnapshot = {
          program: sanitizeProgramForApi(
            {
              ...(fresh.program || {}),
              ...(latest.program || {}),
            },
            template
          ),
          areas: normalizeAreasPayload(areas, template),
          rooms: mergedRooms,
          documentNotes: latest.documentNotes || emptyTipTapNotes(),
        };

        // Snapshot dirty sections up front so edits typed during this save
        // stay queued (don't wipe them on success).
        const sectionsSnapshot = new Set(dirtySectionsRef.current);

        // Routine autosave: only PATCH dirty sections.
        // Submit (waitForIdle) still writes the full sanitized snapshot.
        let patchBody;
        if (waitForIdle) {
          patchBody = {
            program: sanitizedSnapshot.program,
            areas: sanitizedSnapshot.areas,
            rooms: sanitizedSnapshot.rooms.map(
              ({ instance_id, label, answers, notes }) => ({
                instance_id,
                label,
                answers,
                notes,
              })
            ),
            document_notes: sanitizedSnapshot.documentNotes,
          };
        } else {
          patchBody = buildDirtyPatchBody(
            sanitizedSnapshot,
            sectionsSnapshot
          );
          if (!patchBody) {
            sectionsSnapshot.forEach((k) =>
              dirtySectionsRef.current.delete(k)
            );
            dirtyRef.current = dirtySectionsRef.current.size > 0;
            if (!dirtyRef.current) {
              lastSavedFingerprintRef.current = scopeDocFingerprint(
                scopeDocRef.current
              );
              setAutosaveState('Saved');
            }
            return;
          }
        }

        await interiorScopeApi.patchScope(
          effectiveSiteId,
          scopeIdParam,
          patchBody
        );

        // Drop only the sections we just wrote; keep any marked dirty mid-flight.
        if (waitForIdle) {
          dirtySectionsRef.current.clear();
          dirtyRef.current = false;
        } else {
          sectionsSnapshot.forEach((k) => dirtySectionsRef.current.delete(k));
          dirtyRef.current = dirtySectionsRef.current.size > 0;
          // Flush sections edited during this save without waiting another debounce.
          if (dirtyRef.current) pendingSaveRef.current = true;
        }
        lastSavedFingerprintRef.current = scopeDocFingerprint(
          scopeDocRef.current
        );
        lastSavedDocRef.current = sanitizedSnapshot;
        setAutosaveState(dirtyRef.current ? 'Saving…' : 'Saved');
        setError('');

        // Expand already applied new instance ids via applyScope. Only
        // re-GET if rooms are still missing ids after the write.
        const stillNeedIds = (scopeDocRef.current?.rooms || []).some(
          (r) => !(r.instance_id || r.instanceId)
        );
        if (stillNeedIds) {
          skipAutosave.current = true;
          const after = await interiorScopeApi.getScope(
            effectiveSiteId,
            scopeIdParam
          );
          applyScope(after, { preserveLocal: true, markClean: !dirtyRef.current });
          lastSavedFingerprintRef.current = scopeDocFingerprint(
            scopeDocRef.current
          );
          lastSavedDocRef.current = {
            program: after.program || sanitizedSnapshot.program,
            areas: after.areas || sanitizedSnapshot.areas,
            rooms: after.rooms || sanitizedSnapshot.rooms,
            documentNotes:
              after.documentNotes || sanitizedSnapshot.documentNotes,
          };
          setTimeout(() => {
            skipAutosave.current = false;
          }, 400);
        } else if (expanded) {
          // Keep local snapshot as source of truth after expand+patch.
          lastSavedDocRef.current = sanitizedSnapshot;
        }
      } catch (err) {
        setAutosaveState('Save failed');
        setError(formatScopeApiError(err));
      } finally {
        savingRef.current = false;
        if (pendingSaveRef.current && dirtyRef.current) {
          pendingSaveRef.current = false;
          persistPatch(scopeDocRef.current);
        } else {
          pendingSaveRef.current = false;
        }
      }
    },
    [
      applyScope,
      effectiveSiteId,
      readOnly,
      runExpandIfNeeded,
      scopeIdParam,
      template,
    ]
  );

  // Keep a stable ref so scheduleAutosave never depends on persistPatch identity.
  useEffect(() => {
    persistPatchRef.current = persistPatch;
  }, [persistPatch]);

  useEffect(
    () => () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    },
    []
  );

  const onProgramPatch = (patch) => {
    if (readOnly) return;
    const coercedPatch = { ...patch };
    Object.keys(coercedPatch).forEach((key) => {
      if (isApiNumberFieldId(key)) {
        coercedPatch[key] = toUiNumberValue(coercedPatch[key]);
      }
    });
    setScopeDoc((prev) => {
      const nextProgram = coerceProgramNumbersForUi({
        ...(prev?.program || {}),
        ...coercedPatch,
      });
      const unchanged = Object.keys(coercedPatch).every(
        (key) => prev?.program?.[key] === nextProgram[key]
      );
      if (unchanged) return prev;
      markDirty('program');
      return {
        ...prev,
        program: nextProgram,
      };
    });
  };

  const onAreaAnswersPatch = (roomType, patch) => {
    if (readOnly) return;
    const coercedPatch = { ...patch };
    Object.keys(coercedPatch).forEach((key) => {
      if (isStringNotNumberFieldId(key)) {
        coercedPatch[key] = coerceToApiString(coercedPatch[key] ?? '');
        return;
      }
      if (isApiNumberFieldId(key)) {
        coercedPatch[key] = toUiNumberValue(coercedPatch[key]);
      }
    });
    setScopeDoc((prev) => {
      const prevArea = prev?.areas?.[roomType] || {};
      const prevAnswers = prevArea.answers || {};
      const nextAnswers = normalizeSingleSelectAnswers(
        coerceAnswerNumbersForUi({
          ...prevAnswers,
          ...coercedPatch,
        })
      );
      const unchanged = Object.keys(coercedPatch).every(
        (key) => prevAnswers[key] === nextAnswers[key]
      );
      if (unchanged) return prev;
      markDirty(`area:${roomType}`);
      return {
        ...prev,
        areas: {
          ...(prev?.areas || {}),
          [roomType]: {
            ...prevArea,
            room_type: roomType,
            answers: nextAnswers,
          },
        },
      };
    });
  };

  const onRoomAnswersPatch = (instanceId, patch) => {
    if (readOnly || !instanceId) return;
    const coercedPatch = { ...patch };
    Object.keys(coercedPatch).forEach((key) => {
      if (isStringNotNumberFieldId(key)) {
        coercedPatch[key] = coerceToApiString(coercedPatch[key] ?? '');
        return;
      }
      if (isApiNumberFieldId(key)) {
        coercedPatch[key] = toUiNumberValue(coercedPatch[key]);
      }
    });
    setScopeDoc((prev) => {
      let changed = false;
      const rooms = (prev?.rooms || []).map((room) => {
        const id = room.instance_id || room.instanceId;
        if (id !== instanceId) return room;
        const prevAnswers = room.answers || {};
        const nextAnswers = normalizeSingleSelectAnswers(
          coerceAnswerNumbersForUi({
            ...prevAnswers,
            ...coercedPatch,
          })
        );
        const unchanged = Object.keys(coercedPatch).every(
          (key) => prevAnswers[key] === nextAnswers[key]
        );
        if (unchanged) return room;
        changed = true;
        return { ...room, answers: nextAnswers };
      });
      if (!changed) return prev;
      markDirty(`room:${instanceId}`);
      return { ...prev, rooms };
    });
  };

  const onRoomLabelChange = (instanceId, label) => {
    if (readOnly || !instanceId) return;
    setScopeDoc((prev) => {
      let changed = false;
      const rooms = (prev?.rooms || []).map((room) => {
        const id = room.instance_id || room.instanceId;
        if (id !== instanceId) return room;
        if (room.label === label) return room;
        changed = true;
        return { ...room, label };
      });
      if (!changed) return prev;
      markDirty(`room:${instanceId}`);
      return { ...prev, rooms };
    });
  };

  const onDocumentNotesChange = (notes) => {
    if (readOnly) return;
    setScopeDoc((prev) => {
      try {
        if (
          JSON.stringify(prev?.documentNotes || null) ===
          JSON.stringify(notes || null)
        ) {
          return prev;
        }
      } catch {
        /* fall through */
      }
      markDirty('document_notes');
      return {
        ...prev,
        documentNotes: notes,
      };
    });
  };

  const currentIndex = steps.findIndex((s) => s.id === activeStep?.id);
  const hasNext = currentIndex >= 0 && currentIndex < steps.length - 1;

  const canSubmit = useMemo(() => {
    const p = scopeDoc?.program || {};
    return Boolean(
      String(p.client_names || '').trim() ||
        String(p.project_address || '').trim()
    );
  }, [scopeDoc]);

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError('Enter project address or client name in section A');
      setActiveStepId('program:A');
      return;
    }
    if (!effectiveSiteId || !scopeIdParam) return;
    setSubmitting(true);
    setError('');
    // Freeze autosave so a late PATCH cannot rewrite numbers as "".
    skipAutosave.current = true;
    pendingSaveRef.current = false;
    try {
      await persistPatch(scopeDocRef.current || scopeDoc, {
        waitForIdle: true,
      });

      // Build the submit write from the raw server doc + local edits.
      // Server answers still contain "" for empty number fields — those must
      // be overwritten with JSON numbers before /submit.
      const fresh = await interiorScopeApi.getScope(
        effectiveSiteId,
        scopeIdParam
      );
      const local = scopeDocRef.current || scopeDoc || {};

      const areasForEnsure = {};
      const areaKeys = new Set([
        ...Object.keys(fresh.areas || {}),
        ...Object.keys(local.areas || {}),
      ]);
      areaKeys.forEach((roomType) => {
        const known = collectKnownQuestionIdsForRoomType(template, roomType);
        const options = collectQuestionOptionsForRoomType(template, roomType);
        const numberIds = collectNumberFieldIdsForRoomType(template, roomType);
        if (known) {
          known.forEach((id) => {
            if (isApiNumberFieldId(id)) numberIds.add(id);
          });
        }
        if (options) {
          options.forEach((_vals, id) => {
            // Keep dimension/qty ids as numbers even if template has options_ref
            if (!isApiNumberFieldId(id) || isStringNotNumberFieldId(id)) {
              numberIds.delete(id);
            }
          });
        }
        [...numberIds].forEach((id) => {
          if (isStringNotNumberFieldId(id)) numberIds.delete(id);
          if (SINGLE_SELECT_ANSWER_IDS.has(id)) numberIds.delete(id);
          if (isForbiddenAnswerForRoomType(roomType, id)) numberIds.delete(id);
        });
        const merged = {
          ...(fresh.areas?.[roomType]?.answers || {}),
          ...(local.areas?.[roomType]?.answers || {}),
        };
        areasForEnsure[roomType] = {
          answers: finalizeAnswersForRoomType(
            roomType,
            forceAnswerNumbers(
              sanitizeAnswersForApi(merged, known, options, numberIds),
              known,
              options ? new Set(options.keys()) : null
            )
          ),
        };
        // Fields hidden in UI (e.g. main_entrance ceiling_lights_qty) or left
        // blank still must be JSON numbers on submit.
        const forceIds =
          roomType === 'main_entrance'
            ? [
                'door_height',
                'door_width',
                'length',
                'width',
                'ceiling_height',
                'ceiling_lights_qty',
              ]
            : roomType === 'living_drawing'
              ? ['lintel_height', 'lounge_chairs_qty']
              : null;
        if (forceIds) {
          forceIds.forEach((id) => {
            areasForEnsure[roomType].answers[id] = coerceAnswerNumberValue(
              areasForEnsure[roomType].answers[id]
            );
          });
        }
        // Never send dining-only / kitchen-forbidden fields (submit: unknown question_id)
        areasForEnsure[roomType].answers = finalizeAnswersForRoomType(
          roomType,
          areasForEnsure[roomType].answers
        );
      });

      const roomById = new Map();
      (fresh.rooms || []).forEach((room) => {
        const id = room.instance_id || room.instanceId;
        if (id) roomById.set(id, room);
      });
      (local.rooms || []).forEach((room) => {
        const id = room.instance_id || room.instanceId;
        if (!id) return;
        const server = roomById.get(id) || {};
        roomById.set(id, {
          ...server,
          ...room,
          answers: {
            ...(server.answers || {}),
            ...(room.answers || {}),
          },
        });
      });

      const roomsForEnsure = [...roomById.values()]
        .map((room) => {
          const instanceId = room.instance_id || room.instanceId;
          if (!instanceId) return null;
          const roomType = room.room_type;
          const known = collectKnownQuestionIdsForRoomType(template, roomType);
          const options = collectQuestionOptionsForRoomType(template, roomType);
          const numberIds = collectNumberFieldIdsForRoomType(template, roomType);
          if (known) {
            known.forEach((id) => {
              if (isApiNumberFieldId(id)) numberIds.add(id);
            });
          }
          if (options) {
            options.forEach((_vals, id) => {
              if (!isApiNumberFieldId(id) || isStringNotNumberFieldId(id)) {
                numberIds.delete(id);
              }
            });
          }
          [...numberIds].forEach((id) => {
            if (isStringNotNumberFieldId(id)) numberIds.delete(id);
            if (SINGLE_SELECT_ANSWER_IDS.has(id)) numberIds.delete(id);
            if (isForbiddenAnswerForRoomType(roomType, id)) numberIds.delete(id);
          });
          return {
            instance_id: instanceId,
            label: room.label,
            answers: finalizeAnswersForRoomType(
              roomType,
              forceAnswerNumbers(
                sanitizeAnswersForApi(
                  room.answers || {},
                  known,
                  options,
                  numberIds
                ),
                known,
                options ? new Set(options.keys()) : null
              )
            ),
          };
        })
        .filter(Boolean);

      await interiorScopeApi.ensureProgramNumbers(
        effectiveSiteId,
        scopeIdParam,
        sanitizeProgramForApi(
          {
            ...(fresh.program || {}),
            ...(local.program || {}),
          },
          template
        ),
        normalizeAreasPayload(areasForEnsure, template),
        roomsForEnsure
      );

      // Final wire check — if these aren't JSON numbers on GET, submit will fail.
      const CRITICAL = {
        main_entrance: [
          'door_height',
          'door_width',
          'length',
          'width',
          'ceiling_height',
          'ceiling_lights_qty',
        ],
        living_drawing: ['lintel_height', 'lounge_chairs_qty'],
      };
      const programForSubmit = sanitizeProgramForApi(
        {
          ...(fresh.program || {}),
          ...(local.program || {}),
        },
        template
      );

      const restoreProgramThenAreas = async (areasPatch = null) => {
        await interiorScopeApi.patchScope(effectiveSiteId, scopeIdParam, {
          program: programForSubmit,
        });
        if (areasPatch && Object.keys(areasPatch).length) {
          await interiorScopeApi.patchScope(effectiveSiteId, scopeIdParam, {
            areas: areasPatch,
          });
        }
      };

      let probeScope = await interiorScopeApi.getScope(
        effectiveSiteId,
        scopeIdParam
      );
      const readAnswer = (raw, roomType, id) =>
        raw?.areas?.[roomType]?.answers?.[id] ??
        raw?.areas?.[roomType]?.[id];

      const buildCriticalAreasPatch = (scopeSnap) => {
        const areasPatch = {};
        Object.entries(CRITICAL).forEach(([roomType, ids]) => {
          const existing =
            (scopeSnap.raw || scopeSnap)?.areas?.[roomType]?.answers ||
            areasForEnsure[roomType]?.answers ||
            {};
          const answers = { ...existing };
          ids.forEach((id) => {
            if (isForbiddenAnswerForRoomType(roomType, id)) return;
            answers[id] = coerceAnswerNumberValue(answers[id]);
          });
          areasPatch[roomType] = {
            room_type: roomType,
            answers: finalizeAnswersForRoomType(roomType, answers),
          };
        });
        return areasPatch;
      };

      const badProbe = [];
      Object.entries(CRITICAL).forEach(([roomType, ids]) => {
        ids.forEach((id) => {
          const got = readAnswer(probeScope.raw || probeScope, roomType, id);
          if (
            typeof got !== 'number' ||
            !Number.isFinite(got) ||
            !(got > 0)
          ) {
            badProbe.push(
              `${roomType}.${id}=${typeof got}:${JSON.stringify(got)}`
            );
          }
        });
      });

      const areasPatch = buildCriticalAreasPatch(probeScope);
      // Always finish: program first, then areas (areas last).
      await restoreProgramThenAreas(areasPatch);
      probeScope = await interiorScopeApi.getScope(
        effectiveSiteId,
        scopeIdParam
      );

      badProbe.length = 0;
      Object.entries(CRITICAL).forEach(([roomType, ids]) => {
        ids.forEach((id) => {
          const got = readAnswer(probeScope.raw || probeScope, roomType, id);
          if (
            typeof got !== 'number' ||
            !Number.isFinite(got) ||
            !(got > 0)
          ) {
            badProbe.push(
              `${roomType}.${id}=${typeof got}:${JSON.stringify(got)}`
            );
          }
        });
      });

      if (badProbe.length) {
        throw new Error(
          `Cannot submit — area numbers not stored on server: ${badProbe.join('; ')}. Backend may be dropping area answer updates.`
        );
      }

      // Program must still be numbers after the final areas write
      const prog = (probeScope.raw || probeScope)?.program || {};
      const progBad = [
        'bedroom_count',
        'balcony_count',
        'help_room_count',
        'store_room_count',
        'carpet_area_sqft',
        'general_ceiling_height',
        'floor_to_floor_height',
      ].filter((k) => typeof prog[k] !== 'number' || !Number.isFinite(prog[k]));
      if (progBad.length) {
        // Last resort: program-only then areas again
        await restoreProgramThenAreas(buildCriticalAreasPatch(probeScope));
        probeScope = await interiorScopeApi.getScope(
          effectiveSiteId,
          scopeIdParam
        );
        const prog2 = (probeScope.raw || probeScope)?.program || {};
        const stillBad = progBad.filter(
          (k) => typeof prog2[k] !== 'number' || !Number.isFinite(prog2[k])
        );
        if (stillBad.length) {
          throw new Error(
            `Cannot submit — program numbers not stored: ${stillBad
              .map(
                (k) =>
                  `${k}=${typeof prog2[k]}:${JSON.stringify(prog2[k])}`
              )
              .join(', ')}`
          );
        }
      }

      try {
        await interiorScopeApi.submitScope(effectiveSiteId, scopeIdParam, {});
      } catch (submitErr) {
        try {
          const snap = await interiorScopeApi.getScope(
            effectiveSiteId,
            scopeIdParam
          );
          const raw = snap?.raw || snap || {};
          const missing = submitErr.missing || submitErr.responseData?.missing;
          if (Array.isArray(missing) && missing.length) {
            missing.forEach((m) => {
              const section = m.section;
              const qid = m.question_id;
              let got;
              if (section === 'program') {
                got = raw.program?.[qid];
              } else if (raw.areas?.[section]) {
                got =
                  raw.areas[section]?.answers?.[qid] ??
                  raw.areas[section]?.[qid];
              } else {
                // Search all areas/rooms by question id
                Object.values(raw.areas || {}).some((area) => {
                  if (area?.answers && qid in area.answers) {
                    got = area.answers[qid];
                    return true;
                  }
                  if (area && qid in area) {
                    got = area[qid];
                    return true;
                  }
                  return false;
                });
                if (got === undefined) {
                  const room = (raw.rooms || []).find(
                    (r) =>
                      (r.instance_id || r.instanceId) === section ||
                      r.room_type === section
                  );
                  got = room?.answers?.[qid];
                }
              }
              m.got = `${typeof got}:${JSON.stringify(got)}`;
              m.label = `${qid} must be a number (stored ${m.got})`;
            });
            submitErr.responseData = {
              ...(submitErr.responseData || {}),
              missing,
            };
            submitErr.message = `Validation failed: ${missing
              .slice(0, 8)
              .map((m) => m.label)
              .join(', ')}`;
          }
        } catch {
          // ignore enrichment failures
        }
        throw submitErr;
      }
      navigate(
        `/projects/${scopeIdParam}/preview?siteId=${encodeURIComponent(effectiveSiteId)}`,
        { state: { siteId: effectiveSiteId } }
      );
    } catch (err) {
      setError(formatScopeApiError(err));
    } finally {
      skipAutosave.current = false;
      setSubmitting(false);
    }
  };

  const handleReopen = async () => {
    if (!effectiveSiteId || !scopeIdParam) return;
    setSubmitting(true);
    setError('');
    try {
      const reopened = await interiorScopeApi.reopenScope(
        effectiveSiteId,
        scopeIdParam
      );
      applyScope(reopened);
      setAutosaveState('Reopened as draft');
    } catch (err) {
      setError(formatScopeApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    setSubmitting(true);
    try {
      await persistPatch(scopeDoc);
      navigate(`/projects?siteId=${encodeURIComponent(effectiveSiteId)}`, {
        state: { siteId: effectiveSiteId },
      });
    } catch (err) {
      setError(formatScopeApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const pageTitle = readOnly
    ? 'View Scope Sheet'
    : isEditMode
      ? 'Edit Scope Sheet'
      : 'New Scope Sheet';

  return (
    <div className="w-full flex flex-col gap-6">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(280px,340px)_minmax(0,1fr)] gap-6 min-h-[70vh]">
        <Card.Gradient className="p-5 flex flex-col gap-4 min-h-[560px] max-h-[calc(100vh-8rem)]">
          <div>
            <h3 className={typography.h3} style={{ color: '#000000' }}>
              {pageTitle}
            </h3>
            <p className="mt-1 text-xs text-zinc-600 font-['Open_Sans']">
              {template
                ? `${template.title} v${template.version}`
                : 'Loading template…'}
              {scopeDoc?.status ? ` · ${scopeDoc.status}` : ''}
              {autosaveState ? ` · ${autosaveState}` : ''}
            </p>
            {!siteId && sites.length > 0 && !isEditMode && (
              <select
                className="mt-3 w-full h-10 rounded-xl border border-zinc-300 bg-white px-3 text-sm font-['Open_Sans']"
                value={effectiveSiteId}
                onChange={(e) => setResolvedSiteId(e.target.value)}
              >
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <TemplateScopeNav
            steps={steps}
            activeStepId={activeStep?.id}
            completedStepIds={navCompletedStepIds}
            onSelect={goToStep}
            className="flex-1 min-h-0"
          />

          <div className="pt-4 flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              type="button"
              disabled={submitting || loadingProject || !effectiveSiteId}
              onClick={handleSaveDraft}
              className="flex-1 h-11 rounded-xl border border-zinc-300 bg-white/80 px-3 text-sm font-medium text-black font-['Open_Sans'] hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {readOnly ? 'Back to list' : 'Save as Draft'}
            </button>
            {readOnly ? (
              <PrimaryButton
                disabled={submitting}
                onClick={handleReopen}
                className="!flex-1 !w-auto !min-w-0 !h-11 !text-sm"
              >
                Reopen
              </PrimaryButton>
            ) : (
              <PrimaryButton
                disabled={submitting || loadingProject || !canSubmit}
                onClick={handleSubmit}
                className="!flex-1 !w-auto !min-w-0 !h-11 !text-sm"
              >
                {submitting ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
                    Submitting...
                  </span>
                ) : (
                  'Submit'
                )}
              </PrimaryButton>
            )}
          </div>
        </Card.Gradient>

        <Card.Gradient className="p-6 flex flex-col gap-4 min-h-[560px] max-h-[calc(100vh-8rem)] overflow-hidden">
          {loadingProject ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-zinc-600">
              <Loader2 className="w-5 h-5 animate-spin" aria-hidden />
              <span className="text-sm font-['Open_Sans']">
                Loading scope template…
              </span>
            </div>
          ) : (
            <>
              <div ref={formScrollRef} className="flex-1 min-h-0 overflow-y-auto p-1">
                {error && (
                  <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600 font-['Open_Sans']">
                    {error}
                  </div>
                )}
                {template && scopeDoc && activeStep ? (
                  <TemplateScopeForm
                    template={template}
                    step={activeStep}
                    scopeDoc={scopeDoc}
                    readOnly={readOnly}
                    onProgramPatch={onProgramPatch}
                    onAreaAnswersPatch={onAreaAnswersPatch}
                    onRoomAnswersPatch={onRoomAnswersPatch}
                    onRoomLabelChange={onRoomLabelChange}
                    onDocumentNotesChange={onDocumentNotesChange}
                  />
                ) : (
                  !error && (
                    <p className="text-sm text-zinc-600 font-['Open_Sans']">
                      Waiting for template and scope document…
                    </p>
                  )
                )}
              </div>
              {hasNext && !readOnly && (
                <div className="flex justify-end shrink-0">
                  <PrimaryButton
                    onClick={continueToNextSection}
                    className="!w-auto !min-w-[112px] !h-11 !px-5 !text-sm"
                  >
                    Continue to next section
                  </PrimaryButton>
                </div>
              )}
            </>
          )}
        </Card.Gradient>
      </div>
    </div>
  );
};

export default CreateProject;
